// File: src/components/layouts/Core/Grid/Grid.tsx
// Purpose: Layout or container component

import React from "react";
import styled, { useTheme } from "styled-components";
import { AppTheme } from "theme";

export interface GridProps {
  className?: string;
  children: React.ReactNode;
  columns?: number; // Default: 3
  maxColumns?: number; // Limits max columns (defaults to `columns`)
  minColumnWidth?: string; // Minimum column width (e.g., "200px")
  gap?: keyof AppTheme["spacing"]; // ✅ FIXED: reference theme spacing type properly
  rowHeight?: string; // Custom row height (e.g., "auto", "1fr", "200px")
  align?: "start" | "center" | "end" | "stretch"; // Align items
  justify?: "start" | "center" | "end" | "space-between" | "space-around"; // Justify items
  responsive?: boolean; // Enable auto-responsiveness
  autoFit?: boolean; // Enable auto-fit for columns
  dense?: boolean; // Optimize for dense packing
}

const StyledGrid = styled.div<GridProps>`
  display: grid;

  /** ✅ Column Handling: AutoFit vs. Fixed */
  grid-template-columns: ${({ columns, minColumnWidth, autoFit, maxColumns }) =>
    autoFit && minColumnWidth
      ? `repeat(auto-fit, minmax(${minColumnWidth}, 1fr))`
      : `repeat(${Math.min(columns || 3, (maxColumns ?? columns) || 6)}, 1fr)`}; 

  grid-auto-rows: ${({ rowHeight }) => rowHeight || "minmax(auto, 1fr)"};
  gap: ${({ theme, gap }) => (gap ? theme.spacing[gap] : theme.spacing.md)};
  align-items: ${({ align }) => align || "stretch"};
  justify-content: ${({ justify }) => justify || "start"};
  grid-auto-flow: ${({ dense }) => (dense ? "dense" : "row")}; // ✅ Enables Dense Packing

  /** ✅ Responsive Layouts */
  ${({ responsive, theme, maxColumns, columns }) =>
    responsive &&
    `
    @media (max-width: ${theme.breakpoints.ultraWide}) {
      grid-template-columns: repeat(${Math.min(5, (maxColumns ?? columns) || 6)}, 1fr);
    }

    @media (max-width: ${theme.breakpoints.ultraWide}) {
      grid-template-columns: repeat(${Math.min(4, (maxColumns ?? columns) || 6)}, 1fr);
    }

    @media (max-width: ${theme.breakpoints.desktop}) {
      grid-template-columns: repeat(${Math.min(3, (maxColumns ?? columns) || 6)}, 1fr);
    }

    @media (max-width: ${theme.breakpoints.laptop}) {
      grid-template-columns: repeat(${Math.min(2, (maxColumns ?? columns) || 6)}, 1fr);
    }

    @media (max-width: ${theme.breakpoints.tablet}) {
      grid-template-columns: repeat(1, 1fr);
    }

    @media (max-width: ${theme.breakpoints.mobile}) {
      grid-template-columns: repeat(1, 1fr);
    }
  `}
`;

export const Grid: React.FC<GridProps> = ({
  children,
  className,
  responsive = true,
  autoFit = false,
  dense = false,
  columns = 3,
  maxColumns,
  gap = "md",
  ...props
}) => {
  const theme = useTheme(); // ✅ Ensure theme is used

  return (
    <StyledGrid
      className={className}
      columns={columns}
      maxColumns={maxColumns ?? columns}
      responsive={responsive}
      autoFit={autoFit}
      dense={dense}
      gap={gap}
      theme={theme} // ✅ Ensure theme is passed
      {...props}
    >
      {children}
    </StyledGrid>
  );
};